import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CheckboxCellRenderer } from '../AgGrid/CheckboxCellRenderer/CheckboxCellRenderer';
import { EventNameCellRenderer } from '../AgGrid/EventNameCellRenderer';
import { formatDateInCell, formatTimeInCell } from '../../utils/agGridUtils';
import { formatPercentFixed2, integerValueFormatter } from '../../utils/numberUtils';
import ButtonIconRenderer from '../AgGrid/ButtonIconRenderer/ButtonIconRenderer';
import { EditButtonCellRenderer } from '../AgGrid/EditButtonCellRenderer';
import { DeleteButtonCellRenderer } from '../AgGrid/DeleteButtonCellRenderer';
import TableGridItem from '../UI/TableGridItem';
import { useAlertMessage } from '../../hooks/useAlertMessage';
import EditRecordOrSerieDialog from './EditDeleteRecordOrSerieDialog';

const actionColumnDefs = {
    field: 'id',
    headerName: '',
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    maxWidth: 55,
    editable: false,
};

const EventReductionTable = ({
    tableRows,
    selectedRow,
    onRowSelect,
    onRowDelete,
    onRowCopy,
    durationTotal,
    reductionTotal,
    getRecordById,
    onRowEditConfirmation,
    canWrite = true,
}) => {
    const { t } = useTranslation('allDeltas');
    const gridRef = useRef();
    const { dispatchAlert } = useAlertMessage();
    const [showDeleteRecordOrSerieDialog, setShowDeleteRecordOrSerieDialog] = useState(false);
    const [showEditRecordOrSerieDialog, setShowEditRecordOrSerieDialog] = useState(false);

    const pinnedBottomRowData = [
        {
            pinnedRowTotal: t('common.total'),
            duration: durationTotal,
            reduction: reductionTotal,
            id: '',
        },
    ];
    const handleRowDelete = async (params) => {
        onRowSelect(params.data);
        setShowDeleteRecordOrSerieDialog(true);
    };

    const deleteRow = async (deleteSerie = false) => {
        await onRowDelete({ id: selectedRow.id, deleteSerie });
        closeDeleteRowDialog();
    };

    const closeDeleteRowDialog = () => {
        setShowDeleteRecordOrSerieDialog(false);
        onRowSelect(null);
    };

    const handleRowEdit = async (params) => {
        try {
            const record = await getRecordById(params.data.id);
            const serieId = record.eventSerieId || record.reductionSerieId || null;
            const serie = record.eventSerie || record.reductionSerie || null;
            onRowSelect({ ...record, serieId, serie });
            if (record.eventSerieId || record.reductionSerieId) setShowEditRecordOrSerieDialog(true);
            else onRowEditConfirmation();
        } catch (error) {
            console.error('Failed to fetch record for editing:', error);
            dispatchAlert({ message: t('error.fetchRecordEditing'), type: 'error' });
        }
    };

    const closeEditDeleteRecordOrSerieDialog = (isDelete) => {
        onRowSelect(null);
        isDelete ? setShowDeleteRecordOrSerieDialog(false) : setShowEditRecordOrSerieDialog(false);
    };

    const editRow = (editOnlyRecord) => {
        onRowSelect((prevSelectedRow) => ({ ...prevSelectedRow, editOnlyRecord }));
        if (showEditRecordOrSerieDialog) setShowEditRecordOrSerieDialog(false);
        onRowEditConfirmation();
    };

    const copySerie = (serie) => {
        return serie ? { ...serie, id: null } : null;
    };

    const handleRowCopy = async (params) => {
        try {
            const record = await getRecordById(params.data.id);
            const { eventSerie, reductionSerie } = record;

            onRowSelect({
                ...record,
                id: null,
                copyRecord: true,
                serieId: null,
                serie: copySerie(eventSerie) || copySerie(reductionSerie),
                eventSerie: copySerie(eventSerie),
                reductionSerie: copySerie(reductionSerie),
            });

            onRowCopy();
        } catch (error) {
            console.error('Failed to fetch record for copying:', error);
            dispatchAlert({ message: t('error.fetchRecordCopying'), type: 'error' });
        }
    };
    const columnDefs = [
        {
            field: 'location',
            headerName: t('field.city'),
            valueGetter: (params) =>
                params.data.pinnedRowTotal || params?.data?.location?.code + ' - ' + params?.data?.location?.city,
        },
        {
            field: 'worktypes',
            headerName: t('field.worktype'),
            minWidth: 115,
            valueFormatter: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
        },
        {
            field: 'channels',
            headerName: t('field.channel'),
            valueFormatter: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
        },
        {
            field: 'tasks',
            headerName: t('field.task'),
            valueFormatter: ({ value }) => (Array.isArray(value) ? value.join(', ') : value),
        },
        {
            field: 'name',
            headerName: t('field.name'),
            cellRenderer: EventNameCellRenderer,
        },
        {
            field: 'comment',
            headerName: t('field.comment'),
            minWidth: 125,
        },
        {
            field: 'start',
            headerName: t('field.dateFrom'),
            valueGetter: ({ data }) => formatDateInCell({ value: data.start }),
            minWidth: 100,
        },
        {
            field: 'end',
            headerName: t('field.dateTo'),
            valueGetter: ({ data }) => formatDateInCell({ value: data.end }),
            minWidth: 100,
        },
        {
            field: 'start',
            headerName: t('field.timeFrom'),
            valueGetter: ({ data }) => formatTimeInCell({ value: data.start }),
            minWidth: 115,
        },
        {
            field: 'end',
            headerName: t('field.timeTo'),
            valueGetter: ({ data }) => formatTimeInCell({ value: data.end }),
            minWidth: 115,
        },
        {
            field: 'duration',
            filter: 'agNumberColumnFilter',
            headerName: t('field.duration'),
            valueFormatter: integerValueFormatter,
        },
        {
            field: 'reduction',
            headerName: t('field.reduction'),
            filter: 'agNumberColumnFilter',
            // CALL-796: German decimals, and a blank cell no longer renders "undefined %".
            valueFormatter: ({ value }) => formatPercentFixed2(value),
            minWidth: 115,
        },
        {
            field: 'increase',
            headerName: t('field.increase'),
            minWidth: 115,
            cellRenderer: CheckboxCellRenderer,
            cellRendererParams: {
                propertyName: 'increase',
            },
        },
        {
            field: 'productive',
            headerName: t('field.productive'),
            minWidth: 115,
            cellRenderer: CheckboxCellRenderer,
            cellRendererParams: {
                propertyName: 'productive',
            },
        },
        ...(canWrite
            ? [
                  {
                      ...actionColumnDefs,
                      colId: 'copy',
                      cellRenderer: ButtonIconRenderer,
                      cellRendererParams: {
                          icon: ContentCopyIcon,
                          onClick: handleRowCopy,
                      },
                  },
                  {
                      ...actionColumnDefs,
                      colId: 'edit',
                      cellRenderer: EditButtonCellRenderer,
                      cellRendererParams: {
                          clicked: handleRowEdit,
                      },
                  },
                  {
                      ...actionColumnDefs,
                      colId: 'delete',
                      cellRenderer: DeleteButtonCellRenderer,
                      cellRendererParams: {
                          clicked: handleRowDelete,
                      },
                  },
              ]
            : []),
    ];

    const styleRow = (params) => {
        if (params.node.rowPinned) {
            return { fontWeight: 'bold' };
        }
    };

    return (
        <>
            <TableGridItem
                ref={gridRef}
                rowData={tableRows}
                defaultColDef={{
                    editable: false,
                    filter: true,
                    sortable: true,
                    minWidth: 90,
                }}
                columnDefs={columnDefs}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={styleRow}
            ></TableGridItem>
            {showDeleteRecordOrSerieDialog && selectedRow && (
                <EditRecordOrSerieDialog
                    showDialog={showDeleteRecordOrSerieDialog}
                    onClose={() => closeEditDeleteRecordOrSerieDialog(true)}
                    onConfirm={deleteRow}
                    isDelete={true}
                    recordName={selectedRow?.name}
                    serieId={selectedRow?.reductionSerieId || selectedRow?.eventSerieId || null}
                ></EditRecordOrSerieDialog>
            )}
            {showEditRecordOrSerieDialog && selectedRow && (
                <EditRecordOrSerieDialog
                    showDialog={showEditRecordOrSerieDialog}
                    onClose={() => closeEditDeleteRecordOrSerieDialog(false)}
                    onConfirm={editRow}
                    isDelete={false}
                ></EditRecordOrSerieDialog>
            )}
        </>
    );
};

export default EventReductionTable;
